// Combat service for PvE battles

import combatData from './combat.data.json';
import { playSound } from '../utils/sounds.js';

class CombatService {
  constructor() {
    this.currentCombat = null;
    this.combatHistory = this.loadCombatHistory();
  }

  // Get enemies for current location
  getEnemiesForLocation(location) {
    return combatData.enemies[location] || [];
  }

  // Start combat with an enemy
  startCombat(enemyId, location) {
    const enemies = this.getEnemiesForLocation(location);
    const enemyTemplate = enemies.find(e => e.id === enemyId);
    
    if (!enemyTemplate) {
      throw new Error(`Enemy ${enemyId} not found in ${location}`);
    }

    // Create combat instance
    this.currentCombat = {
      id: Date.now().toString(),
      startTime: new Date(),
      location,
      enemy: {
        ...enemyTemplate,
        currentHealth: enemyTemplate.stats.health,
        maxHealth: enemyTemplate.stats.health
      },
      player: {
        currentHealth: 100, // TODO: Get from player stats
        maxHealth: 100,
        attack: 15, // TODO: Get from equipment + level
        defense: 5,
        level: 1
      },
      turn: 'player',
      round: 1,
      log: [],
      status: 'active',
      rewards: null
    };

    this.addCombatLog('¡El combate ha comenzado!', 'info');
    this.saveCombatState();
    
    return this.currentCombat;
  }

  // Player attack action
  async playerAttack() {
    if (!this.currentCombat || this.currentCombat.status !== 'active' || this.currentCombat.turn !== 'player') {
      throw new Error('Invalid combat state for player attack');
    }

    const { player, enemy } = this.currentCombat;
    
    // Calculate hit chance (base 85% + speed difference)
    const hitChance = 0.85 + (player.level * 0.02);
    const hit = Math.random() < hitChance;
    
    if (!hit) {
      // Miss
      const missMessage = this.getRandomMessage('playerMiss');
      this.addCombatLog(missMessage, 'miss');
      await playSound('error');
    } else {
      // Calculate damage
      const baseDamage = player.attack;
      const defense = enemy.stats.defense;
      const critChance = 0.15; // 15% critical chance
      const isCritical = Math.random() < critChance;
      
      let damage = Math.max(1, baseDamage - defense);
      
      if (isCritical) {
        damage = Math.floor(damage * 1.8); // 80% more damage on crit
        const critMessage = this.getRandomMessage('playerCritical');
        this.addCombatLog(`${critMessage} (${damage} daño)`, 'critical');
        await playSound('pickup'); // Use pickup sound for critical
      } else {
        const attackMessage = this.getRandomMessage('playerAttack');
        this.addCombatLog(`${attackMessage} (${damage} daño)`, 'attack');
        await playSound('use');
      }
      
      // Apply damage
      enemy.currentHealth = Math.max(0, enemy.currentHealth - damage);
    }

    // Check if enemy is defeated
    if (enemy.currentHealth <= 0) {
      return this.handleEnemyDefeat();
    }

    // Switch to enemy turn
    this.currentCombat.turn = 'enemy';
    this.saveCombatState();
    
    return this.currentCombat;
  }

  // Enemy attack action
  async enemyAttack() {
    if (!this.currentCombat || this.currentCombat.status !== 'active' || this.currentCombat.turn !== 'enemy') {
      throw new Error('Invalid combat state for enemy attack');
    }

    const { player, enemy } = this.currentCombat;
    
    // Calculate hit chance
    const hitChance = 0.75 + (enemy.level * 0.01);
    const hit = Math.random() < hitChance;
    
    if (!hit) {
      // Miss
      const missMessage = this.getRandomMessage('enemyMiss');
      this.addCombatLog(missMessage, 'dodge');
    } else {
      // Calculate damage
      const baseDamage = enemy.stats.attack;
      const defense = player.defense;
      const critChance = 0.1; // 10% critical chance for enemies
      const isCritical = Math.random() < critChance;
      
      let damage = Math.max(1, baseDamage - defense);
      
      if (isCritical) {
        damage = Math.floor(damage * 1.6); // 60% more damage on crit
        const critMessage = this.getRandomMessage('enemyCritical');
        this.addCombatLog(`${critMessage} (${damage} daño)`, 'enemyCritical');
        await playSound('error');
      } else {
        const attackMessage = this.getRandomMessage('enemyAttack');
        this.addCombatLog(`${attackMessage} (${damage} daño)`, 'enemyAttack');
        await playSound('discard');
      }
      
      // Apply damage
      player.currentHealth = Math.max(0, player.currentHealth - damage);
    }

    // Check if player is defeated
    if (player.currentHealth <= 0) {
      return this.handlePlayerDefeat();
    }

    // Switch to player turn and increment round
    this.currentCombat.turn = 'player';
    this.currentCombat.round += 1;
    this.saveCombatState();
    
    return this.currentCombat;
  }

  // Player flees from combat
  async fleeCombat() {
    if (!this.currentCombat || this.currentCombat.status !== 'active') {
      throw new Error('No active combat to flee from');
    }

    const fleeChance = 0.8; // 80% chance to escape
    const success = Math.random() < fleeChance;
    
    if (success) {
      const fleeMessage = this.getRandomMessage('flee');
      this.addCombatLog(fleeMessage, 'flee');
      this.currentCombat.status = 'fled';
      this.currentCombat.endTime = new Date();
      
      await playSound('error');
      this.finalizeCombat();
      return { success: true, combat: this.currentCombat };
    } else {
      this.addCombatLog('No puedes escapar!', 'fail');
      // Enemy gets a free attack
      this.currentCombat.turn = 'enemy';
      this.saveCombatState();
      return { success: false, combat: this.currentCombat };
    }
  }

  // Handle enemy defeat
  async handleEnemyDefeat() {
    const { enemy } = this.currentCombat;
    
    // Calculate rewards
    const coinReward = Math.floor(
      Math.random() * (enemy.rewards.coins.max - enemy.rewards.coins.min + 1) + 
      enemy.rewards.coins.min
    );
    
    const expReward = enemy.rewards.experience;
    
    // Calculate item drops
    const itemRewards = [];
    for (const itemDrop of enemy.rewards.items) {
      if (Math.random() < itemDrop.chance) {
        itemRewards.push(itemDrop.id);
      }
    }

    this.currentCombat.rewards = {
      coins: coinReward,
      experience: expReward,
      items: itemRewards
    };

    const victoryMessage = this.getRandomMessage('victory');
    this.addCombatLog(victoryMessage, 'victory');
    this.addCombatLog(`Obtienes ${coinReward} monedas y ${expReward} EXP`, 'reward');
    
    if (itemRewards.length > 0) {
      this.addCombatLog(`Objetos obtenidos: ${itemRewards.join(', ')}`, 'reward');
    }

    this.currentCombat.status = 'victory';
    this.currentCombat.endTime = new Date();
    
    await playSound('pickup');
    this.finalizeCombat();
    
    return this.currentCombat;
  }

  // Handle player defeat
  async handlePlayerDefeat() {
    const defeatMessage = this.getRandomMessage('defeat');
    this.addCombatLog(defeatMessage, 'defeat');
    
    this.currentCombat.status = 'defeat';
    this.currentCombat.endTime = new Date();
    
    await playSound('error');
    this.finalizeCombat();
    
    return this.currentCombat;
  }

  // Finalize combat and save to history
  finalizeCombat() {
    if (this.currentCombat) {
      this.combatHistory.push({
        ...this.currentCombat,
        // Don't save the full log to reduce storage size
        logCount: this.currentCombat.log.length
      });
      
      // Keep only last 50 combats
      if (this.combatHistory.length > 50) {
        this.combatHistory = this.combatHistory.slice(-50);
      }
      
      this.saveCombatHistory();
      this.currentCombat = null;
      this.clearCombatState();
    }
  }

  // Get current combat state
  getCurrentCombat() {
    return this.currentCombat;
  }

  // Add message to combat log
  addCombatLog(message, type = 'info') {
    if (this.currentCombat) {
      this.currentCombat.log.push({
        message,
        type,
        timestamp: new Date(),
        round: this.currentCombat.round
      });
    }
  }

  // Get random message by type
  getRandomMessage(type) {
    const messages = combatData.combatMessages[type] || ['Acción de combate'];
    return messages[Math.floor(Math.random() * messages.length)];
  }

  // Get combat history
  getCombatHistory() {
    return this.combatHistory;
  }

  // Get combat statistics
  getCombatStats() {
    const total = this.combatHistory.length;
    const victories = this.combatHistory.filter(c => c.status === 'victory').length;
    const defeats = this.combatHistory.filter(c => c.status === 'defeat').length;
    const fled = this.combatHistory.filter(c => c.status === 'fled').length;
    
    return {
      total,
      victories,
      defeats,
      fled,
      winRate: total > 0 ? ((victories / total) * 100).toFixed(1) : '0.0'
    };
  }

  // Save/load combat state
  saveCombatState() {
    try {
      localStorage.setItem('combat:current', JSON.stringify(this.currentCombat));
      window.dispatchEvent(new CustomEvent('combat:state-changed'));
    } catch (error) {
      console.error('Failed to save combat state:', error);
    }
  }

  loadCombatState() {
    try {
      const saved = localStorage.getItem('combat:current');
      if (saved) {
        this.currentCombat = JSON.parse(saved);
        return this.currentCombat;
      }
    } catch (error) {
      console.error('Failed to load combat state:', error);
    }
    return null;
  }

  clearCombatState() {
    try {
      localStorage.removeItem('combat:current');
      window.dispatchEvent(new CustomEvent('combat:state-changed'));
    } catch (error) {
      console.error('Failed to clear combat state:', error);
    }
  }

  // Save/load combat history
  saveCombatHistory() {
    try {
      localStorage.setItem('combat:history', JSON.stringify(this.combatHistory));
    } catch (error) {
      console.error('Failed to save combat history:', error);
    }
  }

  loadCombatHistory() {
    try {
      const saved = localStorage.getItem('combat:history');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Failed to load combat history:', error);
    }
    return [];
  }
}

// Export singleton instance
export const combatService = new CombatService();

// Auto-load state on initialization
combatService.loadCombatState();